import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { getLoginsData } from "../../../apis/dashboardApi";

const Logins = () => {
  const [fromDate, setFromDate] = useState(
    dayjs().subtract(30, "day").startOf("day"),
  );
  const [toDate, setToDate] = useState(dayjs().endOf("day"));
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = {
          from: fromDate.format("YYYY-MM-DD"),
          to: toDate.format("YYYY-MM-DD"),
        };
        const response = await getLoginsData(params);

        const processedData = response.result.map((item) => ({
          date: dayjs(item.TimePeriod).format("YYYY-MM-DD"),
          loginCount: parseInt(item.LoginCount),
        }));

        const filledData = fillMissingDates(fromDate, toDate, processedData);

        const sortedData = filledData.reverse();

        setChartData(sortedData);
        setTableData(sortedData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [fromDate, toDate]);

  const handleDateChange = (dates) => {
    if (dates) {
      setFromDate(dates[0].startOf("day"));
      setToDate(dates[1].endOf("day"));
    }
  };

  const fillMissingDates = (start, end, data) => {
    const dateMap = new Map(data.map((item) => [item.date, item.loginCount]));
    const filledData = [];

    let currentDate = dayjs(start);
    while (currentDate.isBefore(end) || currentDate.isSame(end, "day")) {
      const dateStr = currentDate.format("YYYY-MM-DD");
      filledData.push({
        date: dateStr,
        loginCount: dateMap.get(dateStr) || 0,
      });
      currentDate = currentDate.add(1, "day");
    }

    return filledData;
  };

  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paginatedData = tableData.slice(startIdx, endIdx);
  const totalPages = Math.ceil(tableData.length / pageSize);

  return (
    <div className="graph-section">
      <h4>Logins Data</h4>
      <div className="filter-section">
        <div className="select-period-wrapper" style={{ width: "100%" }}>
          <label>Select Date Range: </label>
          <br></br>
          <DatePicker.RangePicker
            onChange={handleDateChange}
            disabledDate={(current) =>
              current && current > dayjs().endOf("day")
            }
            defaultValue={[fromDate, toDate]}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {chartData.length === 0 ? (
        <p>No data available for the selected date range.</p>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} barSize={20} margin={{ top: 5, right: 30, left: -25, bottom: 5 }}>
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--one-orange)" stopOpacity={1} />
                <stop offset="100%" stopColor="var(--one-red)" stopOpacity={1} />
              </linearGradient>
            </defs>

            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="loginCount" fill="url(#barGradient)" name="Login Count" />
          </BarChart>
        </ResponsiveContainer>
      )}

      <div style={{ marginTop: "20px" }}>
        <h4>Login Details</h4>
        <table className="styled-table">
          <thead>
            <tr>
              <th>Time Period</th>
              <th>Login Count</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr key={index}>
                <td>{dayjs(row.date).format("YYYY-MM-DD")}</td>
                <td>{row.loginCount}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pagination" style={{ marginTop: "10px" }}>
          <div>
            Page:
            <input
              type="number"
              value={currentPage}
              onChange={(e) => setCurrentPage(Number(e.target.value))}
              min="1"
              max={totalPages}
            />{" "}
            of {totalPages}
          </div>
          <div>
            Page Size:
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Logins;
